import { ImagePaths } from '../../lib/imageUrls.js';

export const sc100CybersecurityArchitectExpert = {
  id: 51,
  slug: 'sc-100-cybersecurity-architect-expert',
  ogImage: '/og/courses/sc-100-cybersecurity-architect-expert.jpg',
  courseCode: 'SC-100-001',
  featured: false,
  title: 'SC-100 Microsoft Cybersecurity Architect Expert',
  subtitle: 'Design enterprise security architectures aligned to Zero Trust, Essential Eight and the ACSC ISM',

  valueProposition: 'The SC-100 is the capstone Microsoft security certification — the only Expert-level exam in the security track. This programme prepares security architects to design Zero Trust access models, govern multi-cloud security postures, evaluate GRC strategies, and advise executive stakeholders. Every design scenario is grounded in Australian context: ASD Essential Eight, ACSC Information Security Manual, Privacy Act 1988, and APRA CPS 234 for regulated entities.',

  duration: '40 hours (5 days intensive or 10 weeks part-time)',
  students: 'New',
  level: 'Advanced',
  organization: 'Microsoft',
  image: ImagePaths.COURSE_1,
  shortDescription: 'SC-100-001 — Microsoft Cybersecurity Architect Expert certification preparation covering Zero Trust design, multi-cloud security, and GRC for Australian security architects.',

  price: {
    aud: 1999,
    currency: 'AUD',
    earlyBird: 1599
  },

  stripeConfig: {
    priceId: 'price_1U3WMoKu1nyc322UHTCVAK6t',
    productId: 'prod_V3deHrZEgPCc3G'
  },

  snapshot: {
    whoItsFor: [
      'Senior security architects designing enterprise-wide Zero Trust programmes',
      'CISO-track engineers preparing for the SC-100 Expert certification',
      'Security leads in regulated Australian industries (finance, healthcare, government)',
      'Architects holding SC-200, SC-300 or SC-401 who want the capstone Expert credential'
    ],
    accreditation: 'Preparation for Microsoft Certified: Cybersecurity Architect Expert (Exam SC-100). Prerequisite: one of SC-200, SC-300, SC-401, or SC-500 Associate.'
  },

  logistics: {
    classSize: 'Maximum 10 participants — architecture design workshops with whiteboard sessions',
    requirements: [
      'Holder of at least one Microsoft Security Associate certification (SC-200, SC-300, SC-401, or SC-500)',
      'Three or more years of hands-on security architecture or engineering experience',
      'Microsoft 365 E5 or Azure subscription with Owner access for design lab exercises',
      'Working knowledge of Microsoft Defender XDR, Entra ID, and Purview'
    ]
  },

  overview: 'The SC-100 Microsoft Cybersecurity Architect Expert programme is the peak of the Microsoft security certification ladder. It moves beyond tool configuration into architectural thinking — how to design a security strategy that spans identity, data, applications, infrastructure, and operations across hybrid and multi-cloud environments. The curriculum maps directly to the four SC-100 domains: design Zero Trust strategy and architecture; evaluate Governance, Risk and Compliance (GRC) technical strategies; design security for infrastructure; and design security for data and applications. In the Australian context, every design scenario references the ASD Essential Eight, the ACSC Information Security Manual, APRA CPS 234 (for banking and insurance architects), and the Privacy Act 1988 obligations that shape data security architecture. The programme assumes holders of the SC-200, SC-300, SC-400, or SC-500 Associate certifications, building architectural depth on top of existing platform knowledge.',

  outcomes: [
    'Design a Zero Trust strategy spanning identity, network, devices, applications, and data for enterprise-scale Australian organisations',
    'Evaluate and recommend GRC technical strategies aligned to ACSC ISM, Essential Eight, and APRA CPS 234',
    'Design a ransomware resilience architecture covering backup strategy, network segmentation, and incident response runbooks',
    'Architect multi-cloud security posture management using Microsoft Defender for Cloud across Azure, AWS, and GCP',
    'Design data security and privacy architectures aligned to Privacy Act 1988 and Australian Privacy Principles',
    'Advise C-suite and board stakeholders on security investment trade-offs and residual risk acceptance',
    'Pass the Microsoft SC-100 Expert-level exam with structured design scenario practice'
  ],

  audience: [
    'Senior security architects owning enterprise Zero Trust and multi-cloud security strategy',
    'Lead security engineers moving from platform implementation into architecture and design',
    'CISO-track practitioners who need to defend investment decisions to boards and audit committees',
    'Security leads in APRA-regulated banking, insurance and superannuation entities working to CPS 234',
    'Commonwealth and state government security architects working within the ACSC Information Security Manual',
    'Consultants and IRAP-adjacent assessors designing Microsoft security architectures for Australian clients'
  ],

  prerequisites: [
    'A current Microsoft Security Associate certification — SC-200, SC-300, SC-401 or SC-500',
    'Three or more years of hands-on security architecture, engineering or consulting experience',
    'Practical working knowledge of Microsoft Defender XDR, Microsoft Entra ID and Microsoft Purview',
    'Familiarity with at least one non-Azure cloud platform (AWS or GCP) for the multi-cloud posture module',
    'Microsoft 365 E5 or Azure subscription with Owner access for the design lab exercises'
  ],

  modules: [
    {
      number: 1,
      title: 'Zero Trust Strategy and Architecture Design',
      duration: '8 hours',
      topics: [
        'Zero Trust principles and the Microsoft Zero Trust framework — identity, devices, network, applications, data, infrastructure',
        'Designing a Zero Trust access model for a hybrid Australian enterprise',
        'Evaluate identity providers, conditional access design, and continuous access evaluation',
        'Network Zero Trust: micro-segmentation, Azure Firewall, Private Link, and SD-WAN considerations',
        'Mapping Zero Trust controls to ASD Essential Eight Maturity Level 2 and 3 requirements'
      ]
    },
    {
      number: 2,
      title: 'Governance, Risk and Compliance Technical Strategy',
      duration: '8 hours',
      topics: [
        'Evaluate regulatory compliance requirements for Australian entities: APRA CPS 234, Privacy Act 1988, Essential Eight',
        'Design a Microsoft Purview compliance architecture for data residency, classification, and retention',
        'Assess and recommend privacy-by-design controls for AI workloads using Microsoft Copilot',
        'Build a risk acceptance framework and design residual risk treatment strategies',
        'Translate GRC requirements into technical architecture decisions and security controls'
      ]
    },
    {
      number: 3,
      title: 'Security Architecture for Infrastructure',
      duration: '8 hours',
      topics: [
        'Design server and container security posture using Defender for Servers and Defender for Containers',
        'Architect privileged access workstations and tiered administration for Essential Eight compliance',
        'Design storage, database, and backup security controls for ransomware resilience',
        'Multi-cloud security posture management: Defender for Cloud, CSPM, and regulatory compliance workbooks',
        'Network security design: DDoS protection, WAF, Azure Firewall Premium, and TLS inspection'
      ]
    },
    {
      number: 4,
      title: 'Security Architecture for Applications and Data',
      duration: '8 hours',
      topics: [
        'Evaluate application security requirements and recommend SDL practices for cloud-native workloads',
        'Design API security, OAuth 2.0 flows, and secret management using Azure Key Vault',
        'Architect data classification and sensitivity labelling using Microsoft Purview Information Protection',
        'Design Microsoft Defender for Cloud Apps policies for SaaS data governance',
        'Privacy Act 1988 breach notification architecture: detection, assessment, and OAIC reporting workflows'
      ]
    },
    {
      number: 5,
      title: 'Security Operations Architecture and Incident Response',
      duration: '8 hours',
      topics: [
        'Design a Microsoft Sentinel SIEM architecture for Australian organisations: connectors, analytics, SOAR playbooks',
        'Architect a threat intelligence integration using Microsoft Defender Threat Intelligence',
        'Design incident response runbooks for ransomware, BEC, and identity compromise scenarios',
        'Build a security operations metrics framework: MTTD, MTTR, and Essential Eight maturity reporting',
        'SC-100 exam preparation: architecture case studies, design scenario walkthroughs, and mock exam'
      ]
    }
  ],

  format: {
    type: 'Live online architecture workshop — 5 days intensive or 10 weeks part-time',
    includes: [
      '40 hours of instructor-led design workshops with whiteboard sessions',
      'Five end-to-end architecture case studies drawn from Australian enterprise and government scenarios',
      'Zero Trust reference architecture templates and design decision records you keep',
      'GRC mapping workbook covering ACSC ISM, Essential Eight, APRA CPS 234 and Privacy Act 1988',
      'Microsoft Defender for Cloud multi-cloud posture lab across Azure, AWS and GCP',
      'Timed SC-100 mock exam with design scenario walkthroughs and answer rationale',
      'Recorded sessions and course materials for 12 months',
      'Certificate of completion and post-course architecture review clinic'
    ]
  },

  nextIntake: {
    date: 'Register your interest — we will confirm the next cohort',
    details: 'Five-day intensive cohorts run quarterly in Sydney, Melbourne and Canberra; the 10-week part-time stream runs one evening per week for distributed teams'
  },

  certification: 'Prepares participants for Microsoft Certified: Cybersecurity Architect Expert via Exam SC-100. Requires prior completion of SC-200, SC-300, SC-401, or SC-500. Exam voucher purchased separately.',

  faqs: [
    {
      question: 'Which prerequisite certification do I need before SC-100?',
      answer: 'You must hold at least one Microsoft Security Associate certification: SC-200 (Security Operations Analyst), SC-300 (Identity and Access Administrator), SC-401 (Information Security Administrator — the exam that replaced SC-400 on 31 May 2025), or SC-500 (Cloud and AI Security Engineer). SC-300 is the most common prerequisite path for identity-focused architects.'
    },
    {
      question: 'Is SC-100 relevant to Australian government and regulated industry roles?',
      answer: 'Highly relevant. Australian government entities under the ISM, APRA-regulated financial institutions under CPS 234, and healthcare organisations under the My Health Records Act all need architects who can translate compliance requirements into Microsoft security architecture decisions — exactly what SC-100 validates.'
    },
    {
      question: 'How does this differ from the SC-200 or SC-300 courses?',
      answer: 'SC-200 and SC-300 focus on implementing and operating specific Microsoft security platforms. SC-100 focuses on architectural design and strategy — the decisions made before tools are configured. The SC-100 programme assumes you already hold one or more Associate certifications and builds architectural reasoning on top of that platform knowledge.'
    },
    {
      question: 'Can the course be delivered to a security architecture team?',
      answer: 'Yes. We regularly run SC-100 programmes for security architecture teams at ASX-listed companies and Commonwealth agencies. Private cohorts can use real organisational design scenarios under NDA, subject to data classification review.'
    }
  ],

  australianContext: true,
  complianceFrameworks: ['ASD Essential Eight', 'ACSC ISM', 'APRA CPS 234', 'Privacy Act 1988', 'OAIC APPs']
};
